'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { transformItineraryForOverview, transformItineraryForTransportLegs } from '~/components/Trip/Overview/TripItinerary';
import { useTripId } from '~/hooks/useTripId';
import type { HeroRouteRecord } from '~/server/trip/getHeroRoutesForTrip';
import type { PlaceImageRecord } from '~/server/trip/getPlaceImagesForTrip';
import type { TripOverviewItem } from '~/services/engine-ts';
import { useOverviewProgressStore } from '~/stores/tpv2/overviewProgressStore';
import { useTripOverviewSummary } from '../hooks/useTripOverviewSummary';
import { buildItemRouteProgress, focusForRouteProgress, measureScrollerCards, routeProgressAtFocus, scrollerFocus, scrollerLeftForFocus } from '../lib/cardRouteProgress';
import { transformOverviewItems } from '../lib/transformOverviewItems';
import { makeHeroRoutesCmsKey, makePlaceImagesCmsKey } from '../overviewPage/HydratedOverview';
import { CompactItineraryScrollerCard } from './CompactItineraryScrollerCard';
import styles from './ExpandedItineraryScroller.module.css';
interface ExpandedItineraryScrollerProps {
  items: TripOverviewItem[];
  legsOnly?: boolean;
}
export interface ExpandedItineraryScrollerHandle {
  scrollToProgress: (progress: number) => void;
}
export const ExpandedItineraryScroller = forwardRef<ExpandedItineraryScrollerHandle, ExpandedItineraryScrollerProps>(function ExpandedItineraryScroller(t0, ref) {
  const $ = _c(25);
  const {
    items,
    legsOnly: t1
  } = t0;
  const legsOnly = t1 === undefined ? false : t1;
  const scrollRef = useRef(null);
  const setProgress = useOverviewProgressStore(_temp);
  const tripId = useTripId();
  const {
    isDateUnset
  } = useTripOverviewSummary();
  const [activeId, setActiveId] = useState(null);
  let t2;
  if ($[0] !== items) {
    t2 = buildItemRouteProgress(items);
    $[0] = items;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const routeProgress = t2;
  let t3;
  let t4;
  if ($[2] !== routeProgress) {
    t3 = () => ({
      scrollToProgress: progress => {
        const el = scrollRef.current;
        if (!el || el.scrollWidth <= el.clientWidth) {
          return;
        }
        const focus = focusForRouteProgress(measureScrollerCards(el), progress, routeProgress);
        if (focus === null) {
          return;
        }
        el.scrollTo({
          left: scrollerLeftForFocus(el, focus),
          behavior: "smooth"
        });
      }
    });
    t4 = [routeProgress];
    $[2] = routeProgress;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useImperativeHandle(ref, t3, t4);
  let t5;
  if ($[5] !== routeProgress || $[6] !== setProgress) {
    t5 = cardEl => {
      const scroller = scrollRef.current;
      if (!scroller) {
        return;
      }
      const cards = measureScrollerCards(scroller);
      const card = cards.find(c => c.id === cardEl.dataset.cardId);
      if (!card) {
        return;
      }
      if (scroller.scrollWidth > scroller.clientWidth) {
        scroller.scrollTo({
          left: scrollerLeftForFocus(scroller, card.start),
          behavior: "smooth"
        });
        return;
      }
      setProgress(routeProgressAtFocus(cards, card.start, routeProgress).progress);
      setActiveId(card.id);
    };
    $[5] = routeProgress;
    $[6] = setProgress;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const scrollToCard = t5;
  let t6;
  if ($[8] !== tripId) {
    t6 = makeHeroRoutesCmsKey(tripId);
    $[8] = tripId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = {
      queryKey: t6,
      enabled: false
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const {
    data: heroRoutesCms
  } = useQuery(t7);
  let t8;
  if ($[12] !== tripId) {
    t8 = makePlaceImagesCmsKey(tripId);
    $[12] = tripId;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = {
      queryKey: t8,
      enabled: false
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data: placeImagesCms
  } = useQuery(t9);
  const itineraryItems = transformOverviewItems(items, heroRoutesCms, {
    mergeLegRuns: !legsOnly,
    cmsPlaces: placeImagesCms
  });
  const {
    groups,
    returnLegId
  } = legsOnly ? transformItineraryForTransportLegs(itineraryItems) : transformItineraryForOverview(itineraryItems);
  let t10;
  if ($[16] !== routeProgress || $[17] !== setProgress) {
    t10 = () => {
      const el_0 = scrollRef.current;
      if (!el_0) {
        return;
      }
      const compute = () => {
        const cards_0 = measureScrollerCards(el_0);
        const {
          activeId: focusedId,
          progress: progress_0
        } = routeProgressAtFocus(cards_0, scrollerFocus(el_0), routeProgress);
        setProgress(progress_0);
        setActiveId(focusedId ?? cards_0[0]?.id ?? null);
      };
      compute();
      el_0.addEventListener("scroll", compute, {
        passive: true
      });
      return () => el_0.removeEventListener("scroll", compute);
    };
    $[16] = routeProgress;
    $[17] = setProgress;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  useEffect(t10, [setProgress, groups.length, routeProgress]);
  const t11 = styles;
  const t12 = styles;
  const t13 = groups.map(group => <div key={group.id} className={styles.group}><div className={styles.groupHeader}><span className={styles.groupDay}>{group.dayLabel}</span>{!isDateUnset && <span className={styles.groupDate}>{group.dateRange}</span>}</div><div className={styles.groupCards}>{group.items.map(item => <CompactItineraryScrollerCard key={item.id} item={item} isReturnLeg={item.id === returnLegId} isActive={item.id === activeId} onCardClick={scrollToCard} />)}</div></div>);
  let t14;
  if ($[19] !== t12.scroller || $[20] !== t13) {
    t14 = <div ref={scrollRef} className={t12.scroller}>{t13}</div>;
    $[19] = t12.scroller;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t11.scrollerClip || $[23] !== t14) {
    t15 = <div className={t11.scrollerClip}>{t14}</div>;
    $[22] = t11.scrollerClip;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  return t15;
});
function _temp(s) {
  return s.setProgress;
}